import { looksLikePlcCode } from '@realplc/types';
import { Message } from '../types';

type RepairAppendixLike = NonNullable<Message['repairAppendix']>;
type RepairCodeVersionLike = NonNullable<RepairAppendixLike['codeVersions']>[number];

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeRepairVersionId(versionId: unknown): string | undefined {
  if (typeof versionId !== 'string' && typeof versionId !== 'number') return undefined;
  const match = String(versionId).trim().match(/^v?(\d+)(?:\.(\d+))?$/i);
  if (!match) return undefined;
  return `v${Number(match[1])}.${Number(match[2] || '0')}`;
}

function getNextRepairVersionId(versionId: string | undefined): string {
  const normalized = normalizeRepairVersionId(versionId) || 'v1.0';
  const match = normalized.match(/^v(\d+)\.(\d+)$/);
  if (!match) return 'v1.1';
  return `v${Number(match[1])}.${Number(match[2]) + 1}`;
}

export function getRepairVersionLabel(versionId: string | undefined): string {
  return (normalizeRepairVersionId(versionId) || 'v1.0').toUpperCase();
}

function getRepairVersionIdFromCode(code: unknown): string | undefined {
  if (typeof code !== 'string') return undefined;
  const matches = Array.from(code.matchAll(/\bRealPLC\s+Version:\s*V(\d+)\.(\d+)\b/gi));
  const latest = matches[matches.length - 1];
  return latest ? normalizeRepairVersionId(`v${latest[1]}.${latest[2]}`) : undefined;
}

function isSuccessfulRepairStatus(status: unknown): boolean {
  return status === 'completed' || status === 'completed_with_warnings';
}

function normalizeCodeVersion(version: RepairCodeVersionLike): RepairCodeVersionLike {
  const id = normalizeRepairVersionId(version.id) || version.id;
  return {
    ...version,
    id,
    label: version.label || getRepairVersionLabel(id),
  };
}

export function shouldRenderPipelineDetails(
  message: Pick<Message, 'offTopic' | 'routingMode'> | null | undefined,
): boolean {
  return message?.offTopic !== true && message?.routingMode === 'thinking';
}

// The definition lives in `@realplc/types` (plcCodeShape.ts) so the server
// judges by the same grammar; re-exported because seven files import it from here.
export { looksLikePlcCode } from '@realplc/types';

/**
 * Whether the server ended code generation without a usable program.
 *
 * The one definition. The browser parses the stream for itself, and its parse
 * can disagree with the server's verdict — it did on 2026-09-20, when reasoning
 * prose was read as a program. Where the two disagree the server wins: it is
 * the side that knows whether a program was ever produced.
 */
export function serverDeclaredNoProgram(
  message: Pick<Message, 'stageBuffers'> | null | undefined,
): boolean {
  return Boolean(message?.stageBuffers?.codeGeneration?.terminalReason);
}

export function isRepairInProgress(
  message: Pick<Message, 'repairAppendix' | 'progressState'> | null | undefined,
): boolean {
  const repairStatus = message?.repairAppendix?.status;
  const progressPhase = message?.progressState?.phase;
  const progressStage = message?.progressState?.stage;
  const isTerminalPhase = progressPhase === 'completed' || progressPhase === 'failed';

  return repairStatus === 'repairing'
    || progressPhase === 'repairing'
    || (progressStage === 'repair' && !isTerminalPhase);
}

export function getCompletedRepairFinalCode(
  repairAppendix: Pick<NonNullable<Message['repairAppendix']>, 'status' | 'finalCode'> | null | undefined,
): string {
  const finalCode = typeof repairAppendix?.finalCode === 'string' ? repairAppendix.finalCode : '';
  if (!finalCode.trim()) {
    return '';
  }

  return isSuccessfulRepairStatus(repairAppendix?.status) ? finalCode : '';
}

export function getRepairCodeVersions(
  repairAppendix: RepairAppendixLike | null | undefined,
  fallbackBaseCode?: string,
): NonNullable<RepairAppendixLike['codeVersions']> {
  const explicitVersions = Array.isArray(repairAppendix?.codeVersions)
    ? repairAppendix.codeVersions.filter(version => hasText(version?.code)).map(normalizeCodeVersion)
    : [];
  if (explicitVersions.length > 0) {
    return explicitVersions;
  }

  const versions: NonNullable<RepairAppendixLike['codeVersions']> = [];
  const baseVersionId = normalizeRepairVersionId(repairAppendix?.baseVersionId)
    || getRepairVersionIdFromCode(fallbackBaseCode)
    || 'v1.0';
  const repairedVersionId = normalizeRepairVersionId(repairAppendix?.repairedVersionId)
    || getRepairVersionIdFromCode(repairAppendix?.finalCode)
    || getNextRepairVersionId(baseVersionId);
  const activeVersionId = normalizeRepairVersionId(repairAppendix?.activeVersionId)
    || (isSuccessfulRepairStatus(repairAppendix?.status) ? repairedVersionId : baseVersionId);
  if (hasText(fallbackBaseCode)) {
    versions.push({
      id: baseVersionId,
      label: getRepairVersionLabel(baseVersionId),
      role: 'base',
      code: fallbackBaseCode,
      applied: activeVersionId === baseVersionId,
    });
  }
  if (hasText(repairAppendix?.finalCode)) {
    versions.push({
      id: repairedVersionId,
      label: getRepairVersionLabel(repairedVersionId),
      role: 'repair',
      code: repairAppendix.finalCode,
      basedOnVersionId: baseVersionId,
      validation: repairAppendix?.finalValidation,
      applied: activeVersionId === repairedVersionId,
    });
  }
  return versions;
}

/**
 * The version the engineer was actually left with.
 *
 * Scanned from the end rather than with `find`, because an appendix persisted
 * before `applied` became exclusive can carry it on the base *and* on the round
 * the loop kept — and of those two, the later one is what shipped. Falls back to
 * the base, never to the tail: the tail of a repair chain is usually the last
 * candidate the loop rejected.
 */
export function findDeliveredVersion(
  versions: NonNullable<RepairAppendixLike['codeVersions']>,
): RepairCodeVersionLike | undefined {
  for (let index = versions.length - 1; index >= 0; index -= 1) {
    if (versions[index]?.applied) return versions[index];
  }
  return versions.find(version => version.role === 'base') || versions[0];
}

export function getRepairBaseVersion(
  message: Pick<Message, 'parsed' | 'repairAppendix'> | null | undefined,
): RepairCodeVersionLike | undefined {
  if (!message) return undefined;
  return getRepairCodeVersions(message.repairAppendix, message.parsed?.code)
    .find(version => version.role === 'base');
}

export function getRepairBaseVersionCode(message: Pick<Message, 'parsed' | 'repairAppendix'> | null | undefined): string {
  if (!message) return '';
  const baseVersion = getRepairBaseVersion(message);
  return baseVersion?.code || message.parsed?.code || '';
}

export function getRepairCandidateVersion(
  repairAppendix: RepairAppendixLike | null | undefined,
  fallbackBaseCode?: string,
): RepairCodeVersionLike | undefined {
  return getRepairCodeVersions(repairAppendix, fallbackBaseCode)
    .find(version => version.role === 'repair' || version.id === repairAppendix?.repairedVersionId);
}

export function getRepairCandidateVersionCode(
  repairAppendix: RepairAppendixLike | null | undefined,
  fallbackBaseCode?: string,
): string {
  const repairVersion = getRepairCandidateVersion(repairAppendix, fallbackBaseCode);
  return repairVersion?.code || '';
}

export function getActiveRepairVersion(
  message: Pick<Message, 'parsed' | 'repairAppendix'> | null | undefined,
): RepairCodeVersionLike | undefined {
  if (!message) return undefined;
  const versions = getRepairCodeVersions(message.repairAppendix, message.parsed?.code);
  const activeVersionId = normalizeRepairVersionId(message.repairAppendix?.activeVersionId);
  return versions.find(version => version.id === activeVersionId)
    // Same definition of "delivered" the diff uses. Two of them in one file is
    // how the base and the round that was kept came to disagree about which one
    // shipped.
    || findDeliveredVersion(versions);
}

export function getActiveRepairVersionCode(
  message: Pick<Message, 'parsed' | 'repairAppendix'> | null | undefined,
): string {
  const activeVersion = getActiveRepairVersion(message);
  if (activeVersion?.code) return activeVersion.code;

  const completedFinalCode = getCompletedRepairFinalCode(message?.repairAppendix);
  return completedFinalCode || message?.parsed?.code || '';
}

export function shouldRenderGeneratedCode(
  message: Pick<Message, 'offTopic' | 'pipeline' | 'parsed' | 'repairAppendix' | 'progressState'> | null | undefined,
): boolean {
  if (!message || message.offTopic === true) {
    return false;
  }

  const code = message.parsed?.code
    || getRepairBaseVersionCode(message)
    || (isSuccessfulRepairStatus(message.repairAppendix?.status) ? getRepairCandidateVersionCode(message.repairAppendix) : '');

  if (!code.trim()) {
    return false;
  }

  return Boolean(message.pipeline) || looksLikePlcCode(code);
}
